import React, {
  Fragment,
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
} from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { render } from 'react-dom';
import ToastMarker from 'partial/components/react-geek-toast';
import { Minus, X } from '@phosphor-icons/react';

export const ModalMarker = () => <div id="modal-root" />;

export const closeModal = () => {
  const root = document.getElementById('modal-root');
  render(<ModalMarker />, root);
};

/* NEW MODAL START */
const ModalContext = createContext();

function ModalTwo({ modal, i }) {
  const { setModals, states } = useContext(ModalContext);
  const [isTransformMinimize, setIsTransformMinimize] = useState(false);
  const { key, hookKey } = modal;

  const prevModalStateRef = useRef(null);
  const modalState = useMemo(() => {
    if (states[hookKey] !== prevModalStateRef.current) {
      prevModalStateRef.current = states[hookKey];
    }
    return states[hookKey];
  }, [states, hookKey]);

  const handleMaximizeModal = () => {
    setIsTransformMinimize(false);
  };

  const handleTransitionEnd = (e) => {
    if (Array.from(e.target.classList).includes('modal-minimize'))
      setIsTransformMinimize(true);
  };

  const handleCloseModal = (modalKey) => (e) => {
    e.stopPropagation();
    setModals((prev) => prev.filter((mdl) => mdl.key !== modalKey));
  };

  useEffect(() => {
    if (!isTransformMinimize) {
      setModals((prev) =>
        prev.map((mdl) => (mdl.key === key ? { ...mdl, status: 'open' } : mdl)),
      );
    }
  }, [isTransformMinimize, key, setModals]);

  return (
    <>
      <div
        className={`border-l border-t border-r border-neutral-200 bg-white mr-1 px-2 py-1
                    cursor-pointer rounded-tl-lg rounded-tr-lg flex items-center relative
                    ${isTransformMinimize ? '' : 'invisible'}`}
      >
        <button
          type="button"
          aria-label="maximze-tab"
          className="absolute inset-0 w-full h-full"
          onClick={handleMaximizeModal}
        />
        <p className="whitespace-nowrap text-sm text-neutral-700">
          {modal.title}
        </p>
        <button
          type="button"
          className="border-l ml-2 pl-2 cursor-pointer relative z-10"
          onClick={handleCloseModal(modal.key)}
        >
          <X size={14} />
        </button>
      </div>
      <div
        className={`modal-animate modal fixed inset-0 bg-neutral-900/55 transition-all duration-100 ease-linear overflow-auto
         ${modal?.status === 'open' ? '' : 'modal-minimize cursor-pointer'}
         ${isTransformMinimize ? 'hidden' : ''}`}
        style={{
          zIndex: 9999,
          right: modal.status === 'minimize' ? `${i * 100}px` : '0',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* eslint-disable-next-line */}
        <div onClick={(e) => e.stopPropagation()}>
          {typeof modal?.content === 'function'
            ? modal?.content(modalState)
            : modal?.content}
        </div>
        {modal.status === 'minimize' && (
          <div className="absolute w-full h-full" />
        )}
      </div>
    </>
  );
}

ModalTwo.defaultProps = {
  modal: {
    hookKey: '',
    title: '',
  },
};

ModalTwo.propTypes = {
  modal: PropTypes.shape({
    title: PropTypes.string,
    status: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    hookKey: PropTypes.string,
    content: PropTypes.instanceOf(Object).isRequired,
  }),
  i: PropTypes.number.isRequired,
};

export function ModalProvider({ children }) {
  const [modals, setModals] = useState([]);
  const [states, setStates] = useState({});

  return (
    /* eslint-disable-next-line react/jsx-no-constructed-context-values */
    <ModalContext.Provider value={{ setModals, modals, states, setStates }}>
      <ToastMarker />
      <ModalMarker />
      <div className="fixed bottom-0 right-0 max-w-full flex flex-row-reverse z-50 overflow-x-scroll">
        {modals.map((modal, i) => (
          <React.Fragment key={modal.key}>
            <ModalTwo modal={modal} i={i} />
          </React.Fragment>
        ))}
      </div>
      {children}
    </ModalContext.Provider>
  );
}

ModalProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export function ModalCard({
  title,
  children,
  onMinimize,
  onClose,
  size,
  containerClassName,
}) {
  const showHeader = useMemo(
    () => title || onClose || onMinimize,
    [title, onClose, onMinimize],
  );
  return (
    <div
      className={cn(`modal-card ${size}`, containerClassName)}
      style={{ height: 'max-content' }}
    >
      {showHeader && (
        <header className="px-5 py-4 flex items-start gap-4 border-b border-neutral-200">
          {title && (
            <h2 className="text-base font-bold text-neutral-900">{title}</h2>
          )}
          <div className="ml-auto flex items-center gap-1">
            {onMinimize && (
              <button
                type="button"
                aria-label="Minimise"
                className="w-7 h-7 inline-flex items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                onClick={onMinimize}
              >
                <Minus size={16} />
              </button>
            )}
            {onClose && (
              <button
                type="button"
                aria-label="Close"
                className="w-7 h-7 inline-flex items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
                onClick={onClose}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </header>
      )}
      {children}
    </div>
  );
}

/*
 * Footer bar shared by all 19 modals: right-aligned actions on a neutral-50
 * strip with a top hairline, secondary then primary.
 */
export function ModalFooter({ children, className }) {
  return (
    <footer
      className={cn(
        'px-5 py-3 flex items-center justify-end gap-2 bg-neutral-50 border-t border-neutral-200',
        className,
      )}
    >
      {children}
    </footer>
  );
}

ModalFooter.defaultProps = { className: '' };

ModalFooter.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

ModalCard.defaultProps = {
  title: '',
  size: 'md',
  onClose: null,
  onMinimize: null,
  containerClassName: '',
};

ModalCard.propTypes = {
  title: PropTypes.string,
  containerClassName: PropTypes.string,
  onMinimize: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.instanceOf(Object),
  ]),
  onClose: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(Object)]),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const useCreateModal = (key = 'modal', state = {}) => {
  const { setModals, setStates } = useContext(ModalContext);
  const prevStateRef = useRef(null);

  useEffect(() => {
    if (JSON.stringify(prevStateRef.current) === JSON.stringify(state)) return;
    prevStateRef.current = state;
    setStates((prev) => ({ ...prev, [key]: state }));
  }, [state, setStates, key]);

  const createModal = ({ title = 'Title', content = () => null }) => {
    const modalKey = `${key}-${Date.now()}`;
    const minimizeModal = () =>
      setModals((prev) =>
        prev.map((modal) =>
          modal.key === modalKey
            ? {
                ...modal,
                status: 'minimize',
              }
            : modal,
        ),
      );

    const closeThisModal = () => {
      setModals((prev) => prev.filter((modal) => modal.key !== modalKey));
    };
    setModals((prev) => [
      ...prev,
      {
        key: modalKey,
        hookKey: key,
        title,
        status: 'open',
        content: content(closeThisModal, minimizeModal),
      },
    ]);
  };
  return createModal;
};

export const useDestroyModal = () => {
  const { setModals, setStates } = useContext(ModalContext);

  const destroyModal = useCallback(() => {
    setModals([]);
    setStates({});
  }, [setModals, setStates]);

  return destroyModal;
};
